/**
 * modalUtils.ts
 * Modal state management utilities and types
 */

import { useState } from 'react';
import type { DisplayFeeConfig } from '../hooks/useFeeHierarchy';

// Clean modal context interface
export interface ModalContext {
  title: string;
  subtitle?: string;
  level?: string; // Deprecated - use nodeId instead
  nodeId?: string; // New: tree node ID for hierarchy operations
  assignmentType?: 'hierarchy' | 'instrument';
  instrumentId?: string;
  hierarchyPath?: string; // Deprecated - maintained for compatibility
  existingFees?: DisplayFeeConfig;
  hierarchyLevel?: 'asset-class' | 'market-segment' | 'sub-asset-class';
  parentAssetClass?: string;
  parentMarketSegment?: string;
  parentSubAssetClass?: string;
  parentNodeId?: string; // New: parent tree node ID for adding sub-levels
}

// Modal types enum for better type discrimination
export enum ModalType {
  NONE = 'none',
  FEE_ASSIGNMENT = 'fee_assignment',
  ASSET_CLASS_SELECTOR = 'asset_class_selector',
  INSTRUMENT_SELECTION = 'instrument_selection'
}

// Hook for clean modal state management
export const useModalState = () => {
  const [context, setContext] = useState<ModalContext | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(ModalType.NONE);

  const openModal = (type: ModalType, data: ModalContext) => {
    setContext(data);
    setActiveModal(type);
  };

  const closeModal = () => {
    setContext(null);
    setActiveModal(ModalType.NONE);
  };

  return {
    context,
    activeModal,
    openModal,
    closeModal,
    isOpen: (type: ModalType) => activeModal === type
  };
};